import { useTheme } from "next-themes";
import { Toaster as Sonner } from "sonner";
import { LANE_BOTTOM, RIGHT_MARGIN } from "@/components/chat/SidebarChat/chatWidgetLayout";
import {
  FRANKLIN_UPDATE_CARD_CLASS_NAME,
  FRANKLIN_UPDATE_CLOSE_CLASS_NAME,
  FRANKLIN_UPDATE_CLOSE_GUTTER_CLASS_NAME,
  FRANKLIN_UPDATE_DESCRIPTION_CLASS_NAME,
  FRANKLIN_UPDATE_MAX_WIDTH,
  FRANKLIN_UPDATE_PRIMARY_ACTION_CLASS_NAME,
  FRANKLIN_UPDATE_SECONDARY_ACTION_CLASS_NAME,
  FRANKLIN_UPDATE_SIZE_CLASS_NAME,
  FRANKLIN_UPDATE_TEXT_CLASS_NAME,
} from "@/components/chat/SidebarChat/franklinUpdateVisual";
import {
  toastLeftPosition,
  toastRightPosition,
  toastZIndex,
} from "@/components/layout/bottomRightStack";
import { FranklinLogo } from "@/components/ui/franklin-logo";
import { cn } from "@/lib/utils";

type ToasterProps = React.ComponentProps<typeof Sonner>;

/**
 * The app's only toaster. Anchored bottom-right, above the collapsed chat pill
 * AND above the bottom-right lane, so a toast never covers either.
 *
 * `offset` is a single value in sonner 1.x, so it sets the right inset
 * (RIGHT_MARGIN, matching the widget); the bottom inset is raised via the
 * `style` prop — sonner spreads it last into its own inline style, so it wins
 * over the library's zero-specificity `:where()` bottom rule. The lane
 * publishes its own height as `--bottom-right-lane` (0px when empty), which is
 * why toasts slide up as upload/nudge cards appear instead of landing on them.
 * See `components/layout/BottomRightLane.tsx`.
 *
 * Each toast uses the same Franklin update card, logo, type and buttons as the
 * chat nudges (`franklinUpdateVisual.ts`). Toasts are `unstyled`: sonner's own
 * button rule (`[data-styled=true] [data-button]`) outranks any utility class,
 * which is what kept the action button from ever turning red. The toast is a
 * grid — logo | message on the first row, action then cancel on a second row
 * under the message — and shrink-wraps between the shared min/max widths,
 * right-aligned in a toaster as wide as the max.
 *
 * When the panel opens, the corner contract moves this column beside its live
 * x-position and steps it below the panel as a collision failsafe.
 * See `components/layout/bottomRightStack.ts`.
 */
const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();
  const franklinIcon = <FranklinLogo size="xs" showBorder={false} />;

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      position="bottom-right"
      offset={RIGHT_MARGIN}
      className="toaster group"
      closeButton
      expand
      gap={8}
      icons={{
        success: franklinIcon,
        info: franklinIcon,
        warning: franklinIcon,
        error: franklinIcon,
        loading: <FranklinLogo size="xs" showBorder={false} animated />,
      }}
      // sonner spreads `style` after its own `--width`/`--offset`, so ours wins.
      style={
        {
          bottom: `calc(${LANE_BOTTOM}px + var(--bottom-right-lane, 0px))`,
          left: toastLeftPosition,
          right: toastRightPosition,
          zIndex: toastZIndex,
          "--width": `${FRANKLIN_UPDATE_MAX_WIDTH}px`,
        } as React.CSSProperties
      }
      toastOptions={{
        unstyled: true,
        classNames: {
          toast: cn(
            FRANKLIN_UPDATE_CARD_CLASS_NAME,
            FRANKLIN_UPDATE_SIZE_CLASS_NAME,
            // Mobile: sonner's own rule stretches every toast across the screen.
            "group/toast right-0 grid max-w-full grid-cols-[auto_auto_1fr] items-start gap-y-2"
          ),
          icon: "col-start-1 row-start-1 m-0 mr-2.5 flex h-6 w-6 shrink-0 items-center",
          content: cn(
            "col-[2/-1] row-start-1 min-w-0 gap-0.5",
            FRANKLIN_UPDATE_CLOSE_GUTTER_CLASS_NAME
          ),
          title: FRANKLIN_UPDATE_TEXT_CLASS_NAME,
          description: FRANKLIN_UPDATE_DESCRIPTION_CLASS_NAME,
          closeButton: cn(
            FRANKLIN_UPDATE_CLOSE_CLASS_NAME,
            "left-auto transform-none border-0 bg-transparent shadow-none"
          ),
          actionButton: cn(FRANKLIN_UPDATE_PRIMARY_ACTION_CLASS_NAME, "col-start-2 row-start-2"),
          cancelButton: cn(
            FRANKLIN_UPDATE_SECONDARY_ACTION_CLASS_NAME,
            "col-start-3 row-start-2 ml-2"
          ),
          error: "border-destructive/30 [&_[data-title]]:text-destructive",
          warning: "border-warning/40",
          success: "border-success/30",
        },
      }}
      {...props}
    />
  );
};

export { Toaster };
